/**
 * Admin Orders Management — Dynamic Database / Store Integration with Order Details Inspector.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import {
  ShieldAlert,
  Inbox,
  Eye,
  X,
  Package,
  MapPin,
  CreditCard,
  User,
  Calendar,
  Truck,
} from "lucide-react";
import { useCurrentUser } from "@/hooks/use-auth";
import { formatPrice } from "@/components/forms/price-display";
import { siteConfig } from "@/config/site";
import { LoadingSkeleton } from "@/components/common/loading-skeleton";
import {
  fetchAdminOrdersFromSupabase,
  updateOrderStatusInStore,
  type AdminOrderRecord,
} from "@/features/admin/orders/store/admin-orders-store";
import { supabase } from "@/lib/supabase";

export const Route = createFileRoute("/admin/orders")({
  head: () => ({
    meta: [
      { title: `Order Management — Admin — ${siteConfig.name}` },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminOrders,
});

function AdminOrders() {
  const { isAuthenticated, isAdmin, isLoading, user } = useCurrentUser();
  const [orders, setOrders] = useState<AdminOrderRecord[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>("ALL");
  const [selectedOrder, setSelectedOrder] = useState<AdminOrderRecord | null>(null);

  const loadOrders = () => {
    fetchAdminOrdersFromSupabase().then((data) => {
      setOrders(data);
    });
  };

  useEffect(() => {
    loadOrders();

    const channelName = `admin-orders-${Math.random().toString(36).substring(2, 9)}`;
    const channel = supabase
      .channel(channelName)
      .on("postgres_changes", { event: "*", schema: "public", table: "orders" }, () => {
        loadOrders();
      })
      .subscribe();

    const handleLocalUpdate = () => {
      loadOrders();
    };

    window.addEventListener("orders_updated", handleLocalUpdate);
    window.addEventListener("storage", handleLocalUpdate);

    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel("orders_sync");
      bc.onmessage = () => {
        loadOrders();
      };
    } catch {
      // Ignored
    }

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener("orders_updated", handleLocalUpdate);
      window.removeEventListener("storage", handleLocalUpdate);
      bc?.close();
    };
  }, []);

  if (isLoading) {
    return (
      <div className="section-container py-32 text-center">
        <LoadingSkeleton className="h-64 w-full max-w-3xl mx-auto" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="section-container max-w-xl py-32 text-center">
        <ShieldAlert className="mx-auto h-12 w-12 text-amber-500" />
        <h1 className="mt-4 font-display text-2xl">Authentication Required</h1>
        <p className="mt-2 text-xs text-muted-foreground">
          Please sign in to access order management.
        </p>
        <Link
          to="/account/login"
          className="mt-6 inline-flex min-h-11 items-center bg-primary px-6 text-xs uppercase tracking-wider text-primary-foreground hover:opacity-90"
        >
          Sign In
        </Link>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="section-container max-w-xl py-32 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <ShieldAlert className="h-8 w-8" />
        </div>
        <p className="eyebrow mt-6 text-destructive">403 Forbidden</p>
        <h1 className="mt-2 font-display text-3xl">Access Denied</h1>
        <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
          Your account (<strong className="text-foreground">{user?.email}</strong>) does not have
          administrator privileges.
        </p>
        <div className="mt-8 flex justify-center gap-4">
          <Link
            to="/account"
            className="inline-flex min-h-11 items-center border border-border px-6 text-xs uppercase tracking-wider hover:bg-secondary"
          >
            Return to My Account
          </Link>
        </div>
      </div>
    );
  }

  const activeOrders = orders.filter((o) => o.status !== "CANCELLED");
  const pendingOrders = orders.filter((o) => o.status === "PENDING");
  const processingOrders = orders.filter((o) => o.status === "PROCESSING");
  const shippedOrders = orders.filter((o) => o.status === "SHIPPED");
  const deliveredOrders = orders.filter((o) => o.status === "DELIVERED");
  const cancelledOrders = orders.filter((o) => o.status === "CANCELLED");

  const filtered =
    filterStatus === "ALL"
      ? activeOrders
      : filterStatus === "CANCELLED"
        ? cancelledOrders
        : orders.filter((o) => o.status === filterStatus);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "CANCELLED":
        return "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300 border border-rose-200 dark:border-rose-800";
      case "DELIVERED":
        return "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800";
      case "PROCESSING":
        return "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300 border border-blue-200 dark:border-blue-800";
      case "SHIPPED":
        return "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 border border-amber-200 dark:border-amber-800";
      case "PENDING":
      default:
        return "bg-stone-100 text-stone-800 dark:bg-stone-800 dark:text-stone-300 border border-stone-200 dark:border-stone-700";
    }
  };

  const tabs = [
    { id: "ALL", label: "ALL", count: activeOrders.length },
    { id: "PENDING", label: "PENDING", count: pendingOrders.length },
    { id: "PROCESSING", label: "PROCESSING", count: processingOrders.length },
    { id: "SHIPPED", label: "SHIPPED", count: shippedOrders.length },
    { id: "DELIVERED", label: "DELIVERED", count: deliveredOrders.length },
    { id: "CANCELLED", label: "CANCELLED", count: cancelledOrders.length },
  ];

  const updateStatus = async (id: string, newStatus: AdminOrderRecord["status"]) => {
    const updated = await updateOrderStatusInStore(id, newStatus);
    setOrders(updated);
    if (selectedOrder?.id === id) {
      setSelectedOrder({ ...selectedOrder, status: newStatus });
    }
  };

  return (
    <div className="space-y-8 pb-16">
      <div className="border-b pb-6">
        <p className="eyebrow text-xs text-primary font-semibold">Fulfillment Console</p>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold text-foreground">
          Order Management
        </h1>
      </div>

      {/* Tabs Bar */}
      <div className="flex flex-wrap gap-3 sm:gap-4 border-b pb-4 text-xs font-bold uppercase tracking-wider">
        {tabs.map((tab) => {
          const isSelected = filterStatus === tab.id;
          const isCancelledTab = tab.id === "CANCELLED";
          return (
            <button
              key={tab.id}
              onClick={() => setFilterStatus(tab.id)}
              className={`flex items-center gap-1.5 transition-colors pb-4 -mb-4.5 ${
                isSelected
                  ? isCancelledTab
                    ? "font-bold text-rose-700 border-b-2 border-rose-700"
                    : "font-bold text-emerald-800 border-b-2 border-emerald-800"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <span>{tab.label}</span>
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  isSelected
                    ? isCancelledTab
                      ? "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300"
                      : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                    : "bg-secondary text-muted-foreground"
                }`}
              >
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Orders Table */}
      <div className="border border-border/80 bg-card rounded-2xl overflow-hidden shadow-xs">
        {filtered.length === 0 ? (
          <div className="py-20 text-center text-muted-foreground">
            <Inbox className="mx-auto h-12 w-12 text-muted-foreground/50" strokeWidth={1} />
            <p className="mt-3 text-sm font-semibold text-foreground">
              {filterStatus === "CANCELLED" ? "No cancelled orders" : "No orders found"}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {filterStatus === "CANCELLED"
                ? "Orders cancelled by administrators or customers will appear here."
                : "Customer checkout orders will appear here automatically."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs font-medium border-collapse">
              <thead className="border-b uppercase text-muted-foreground tracking-wider bg-secondary/30 text-[11px] font-bold">
                <tr>
                  <th className="py-4 px-5">Order ID</th>
                  <th className="py-4 px-5">Customer</th>
                  <th className="py-4 px-5">Items</th>
                  <th className="py-4 px-5">Grand Total</th>
                  <th className="py-4 px-5">Status</th>
                  <th className="py-4 px-5">Date</th>
                  <th className="py-4 px-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {filtered.map((order) => (
                  <tr
                    key={order.id}
                    onClick={() => setSelectedOrder(order)}
                    className="hover:bg-secondary/20 transition-colors cursor-pointer group"
                  >
                    <td className="py-4 px-5 font-mono font-bold text-foreground group-hover:text-emerald-800">
                      {order.id}
                    </td>

                    <td className="py-4 px-5">
                      <p className="font-bold text-foreground text-sm">{order.customerName}</p>
                      <p className="text-[11px] text-muted-foreground">{order.email}</p>
                    </td>

                    <td className="py-4 px-5">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedOrder(order);
                        }}
                        className="inline-flex items-center gap-1 font-bold text-xs text-foreground hover:text-emerald-800 bg-secondary/60 hover:bg-secondary px-2.5 py-1 rounded-lg border transition-colors"
                      >
                        <Package className="h-3.5 w-3.5 text-emerald-800" />
                        {order.items?.reduce((total, item) => total + item.qty, 0) || 1} items
                      </button>
                    </td>

                    <td className="py-4 px-5 font-display font-bold text-sm text-foreground tabular-nums">
                      {formatPrice(order.total)}
                    </td>

                    <td className="py-4 px-5">
                      <span
                        className={`inline-block rounded-full px-3 py-1 text-[11px] font-bold ${getStatusBadge(
                          order.status,
                        )}`}
                      >
                        {order.status}
                      </span>
                    </td>

                    <td className="py-4 px-5 text-muted-foreground">{order.date}</td>

                    <td className="py-4 px-5 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setSelectedOrder(order)}
                          className="px-3 py-1.5 border border-border rounded-xl text-xs font-bold text-foreground hover:bg-secondary flex items-center gap-1.5 transition-colors"
                        >
                          <Eye className="h-3.5 w-3.5 text-emerald-800" /> View Order
                        </button>

                        <select
                          value={order.status}
                          onChange={(e) =>
                            updateStatus(order.id, e.target.value as AdminOrderRecord["status"])
                          }
                          className="border border-border bg-background px-2.5 py-1 rounded-xl text-xs font-semibold outline-none focus:ring-1 focus:ring-emerald-800"
                        >
                          <option value="PENDING">PENDING</option>
                          <option value="PROCESSING">PROCESSING</option>
                          <option value="SHIPPED">SHIPPED</option>
                          <option value="DELIVERED">DELIVERED</option>
                          <option value="CANCELLED">CANCELLED</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ORDER DETAILS INSPECTOR MODAL */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-background border border-border w-full max-w-3xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="p-5 border-b border-border flex items-center justify-between bg-card">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-2xl bg-emerald-900 text-white font-bold flex items-center justify-center text-sm shadow-md">
                  VRD
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-2xl font-bold text-foreground">
                      Order {selectedOrder.id}
                    </h3>
                    <span
                      className={`text-xs font-bold px-3 py-0.5 rounded-full ${getStatusBadge(
                        selectedOrder.status,
                      )}`}
                    >
                      {selectedOrder.status}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-2 font-mono">
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>Placed on {selectedOrder.date}</span>
                  </p>
                </div>
              </div>

              <button
                onClick={() => setSelectedOrder(null)}
                className="p-2 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-xl transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-6 flex-1">
              {/* 1. Customer & Shipping Info Card */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-card border border-border p-4 rounded-2xl shadow-xs">
                <div className="space-y-1.5">
                  <span className="eyebrow text-xs font-bold text-emerald-800 flex items-center gap-1.5 uppercase tracking-wider">
                    <User className="h-3.5 w-3.5" /> Customer Details
                  </span>
                  <p className="font-bold text-sm text-foreground">{selectedOrder.customerName}</p>
                  <p className="text-xs text-muted-foreground">{selectedOrder.email}</p>
                  {selectedOrder.phone && (
                    <p className="text-xs font-mono text-muted-foreground">{selectedOrder.phone}</p>
                  )}
                </div>

                <div className="space-y-1.5 border-t md:border-t-0 md:border-l border-border pt-3 md:pt-0 md:pl-4">
                  <span className="eyebrow text-xs font-bold text-emerald-800 flex items-center gap-1.5 uppercase tracking-wider">
                    <MapPin className="h-3.5 w-3.5" /> Shipping Address
                  </span>
                  <p className="text-xs text-foreground font-semibold">
                    {selectedOrder.shippingAddress?.fullName || selectedOrder.customerName}
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {selectedOrder.shippingAddress?.address || "Delivery Address"},{" "}
                    {selectedOrder.shippingAddress?.city}, {selectedOrder.shippingAddress?.state} -{" "}
                    {selectedOrder.shippingAddress?.pincode}
                  </p>
                </div>
              </div>

              {/* 2. ORDERED ITEMS LIST (THE KEY REQUEST) */}
              <div className="space-y-3">
                <span className="eyebrow text-xs font-bold text-muted-foreground flex items-center gap-1.5 uppercase tracking-wider">
                  <Package className="h-4 w-4 text-emerald-800" /> Items Ordered (
                  {selectedOrder.items?.reduce((total, item) => total + item.qty, 0) || 1})
                </span>

                <div className="bg-card border border-border/80 rounded-2xl divide-y divide-border/60 overflow-hidden shadow-xs">
                  {!selectedOrder.items || selectedOrder.items.length === 0 ? (
                    <div className="p-4 text-xs text-muted-foreground">
                      Standard Plant Item (Catalogue Item)
                    </div>
                  ) : (
                    selectedOrder.items.map((item, idx) => (
                      <div key={idx} className="p-4 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3.5">
                          <img
                            src={item.image || "/plantpicture/p1/5.webp"}
                            alt={item.name}
                            className="h-14 w-12 object-cover rounded-xl border bg-secondary shrink-0 shadow-2xs"
                          />
                          <div>
                            <h4 className="font-bold text-sm text-foreground">{item.name}</h4>
                            <p className="text-xs font-mono text-muted-foreground">
                              SKU: {item.sku || `VRD-${item.id}`}
                            </p>
                            <p className="text-xs font-semibold text-emerald-800 mt-0.5">
                              Quantity: {item.qty} ×{" "}
                              {formatPrice(
                                item.unitPrice || item.totalPrice || selectedOrder.total,
                              )}
                            </p>
                          </div>
                        </div>

                        <div className="text-right">
                          <span className="font-display font-bold text-base text-foreground tabular-nums">
                            {formatPrice((item.qty || 1) * (item.unitPrice || selectedOrder.total))}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* 3. Cost Summary & Payment Info */}
              <div className="bg-secondary/30 border border-border p-4 rounded-2xl space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground font-semibold flex items-center gap-1.5">
                    <CreditCard className="h-3.5 w-3.5 text-primary" /> Payment Method
                  </span>
                  <span className="font-bold text-foreground font-mono uppercase">
                    {selectedOrder.paymentMethod || "RAZORPAY 1-CLICK"}
                  </span>
                </div>

                <div className="flex items-center justify-between border-t border-border/60 pt-2 font-bold text-sm text-foreground">
                  <span>Grand Total</span>
                  <span className="font-display text-lg text-emerald-800">
                    {formatPrice(selectedOrder.total)}
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-border bg-card flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-muted-foreground uppercase">
                  Update Status:
                </span>
                <select
                  value={selectedOrder.status}
                  onChange={(e) =>
                    updateStatus(selectedOrder.id, e.target.value as AdminOrderRecord["status"])
                  }
                  className="bg-background border border-border px-3 py-1.5 rounded-xl text-xs font-bold text-foreground outline-none focus:ring-1 focus:ring-emerald-800"
                >
                  <option value="PENDING">PENDING</option>
                  <option value="PROCESSING">PROCESSING</option>
                  <option value="SHIPPED">SHIPPED</option>
                  <option value="DELIVERED">DELIVERED</option>
                  <option value="CANCELLED">CANCELLED</option>
                </select>
              </div>

              <button
                onClick={() => setSelectedOrder(null)}
                className="px-5 py-2.5 bg-emerald-800 hover:bg-emerald-700 text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
